import {
  Clock,
  Mic,
  SendHorizontal,
  Smile,
  Trash2,
  Upload,
  MapPin,
} from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { createPortal } from "react-dom";
import EmojiPicker, { type EmojiClickData } from "emoji-picker-react";

interface Props {
  text: string;
  setText: Dispatch<SetStateAction<string>>;
  onSubmit: () => void;
  onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAudioSend?: (audio: Blob) => void;
  onAudioError?: (message: string) => void;
  onLocationSend?: () => void;
  canSendLocation?: boolean;
  disabled?: boolean;
  blocked?: boolean;
  blockedReason?: string;
  isUploadingFile?: boolean;
}

const AUDIO_OGG_OPUS_MIME_TYPE = "audio/ogg;codecs=opus";

export function MessageInput({
  text,
  setText,
  onSubmit,
  onUpload,
  onAudioSend,
  onAudioError,
  onLocationSend,
  canSendLocation = false,
  disabled = false,
  blocked = false,
  blockedReason,
  isUploadingFile = false,
}: Props) {
  const pickerRef = useRef<HTMLDivElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const pendingSendRef = useRef(false);

  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return window.matchMedia("(max-width: 768px)").matches;
  });

  function resolveSupportedMimeType() {
    if (!window.MediaRecorder?.isTypeSupported) {
      return null;
    }

    return MediaRecorder.isTypeSupported(AUDIO_OGG_OPUS_MIME_TYPE)
      ? AUDIO_OGG_OPUS_MIME_TYPE
      : null;
  }

  function handleEmojiClick(emoji: EmojiClickData) {
    setText((prev: string) => {
      return prev + emoji.emoji;
    });
    setShowEmojiPicker(false);
  }

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(e.target as Node) &&
        !emojiButtonRef.current?.contains(e.target as Node)
      ) {
        setShowEmojiPicker(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const handleChange = (event: MediaQueryListEvent) => {
      setIsMobileViewport(event.matches);
    };

    setIsMobileViewport(mediaQuery.matches);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  function autoResize() {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = "auto";

    const lineHeight = 20; // altura aproximada de uma linha
    const maxHeight = lineHeight * 4;

    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
  }

  useEffect(() => {
    if (showEmojiPicker && emojiButtonRef.current) {
      const rect = emojiButtonRef.current.getBoundingClientRect();

      setPickerPosition({
        top: rect.top - 420, // altura aproximada do picker
        left: rect.left,
      });
    }
  }, [showEmojiPicker]);

  useEffect(() => {
    if (blocked) {
      setShowEmojiPicker(false);
    }
  }, [blocked]);

  useEffect(() => {
    if (!isRecording) return;
    const interval = window.setInterval(() => {
      setRecordingSeconds((prev) => prev + 1);
    }, 1000);

    return () => window.clearInterval(interval);
  }, [isRecording]);

  useEffect(() => {
    return () => {
      pendingSendRef.current = false;
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "inactive"
      ) {
        mediaRecorderRef.current.stop();
      }
      stopStreamTracks();
    };
  }, []);

  function stopStreamTracks() {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    mediaRecorderRef.current = null;
  }

  async function startRecording() {
    if (disabled || blocked || isRecording) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      onAudioError?.("Seu navegador não suporta gravação de áudio.");
      return;
    }

    try {
      const mimeType = resolveSupportedMimeType();
      if (!mimeType) {
        onAudioError?.("Seu navegador não suporta gravação em áudio OGG Opus.");
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType });

      audioChunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      recorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, {
          type: AUDIO_OGG_OPUS_MIME_TYPE,
        });
        audioChunksRef.current = [];
        stopStreamTracks();
        setIsRecording(false);
        setRecordingSeconds(0);

        if (!pendingSendRef.current) {
          pendingSendRef.current = false;
          return;
        }

        pendingSendRef.current = false;

        if (!blob.size) {
          onAudioError?.("Não foi possível gravar o áudio.");
          return;
        }

        onAudioSend?.(blob);
      };

      mediaStreamRef.current = stream;
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecordingSeconds(0);
      setIsRecording(true);
    } catch (err) {
      stopStreamTracks();
      onAudioError?.("Não foi possível acessar o microfone.");
    }
  }

  function stopRecording(shouldSend: boolean) {
    const recorder = mediaRecorderRef.current;

    if (!recorder || recorder.state === "inactive") {
      pendingSendRef.current = false;
      return;
    }

    pendingSendRef.current = shouldSend;
    recorder.stop();
  }

  const hasText = text.trim().length > 0;
  const isBlocked = disabled || blocked;
  const inputDisabled = isBlocked || isRecording || isUploadingFile;
  const recordingTime = `${String(Math.floor(recordingSeconds / 60)).padStart(
    2,
    "0",
  )}:${String(recordingSeconds % 60).padStart(2, "0")}`;

  return (
    <>
      {blockedReason && (
        <div className="flex items-start gap-2 border-t border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
          <Clock className="h-4 w-4 mt-0.5 text-amber-600" />
          <span>{blockedReason}</span>
        </div>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (isBlocked || isRecording || !hasText) return;
          onSubmit();
          setShowEmojiPicker(false);
        }}
        className="flex items-center gap-2 border-t border-slate-200 bg-slate-50 p-3"
        title={blockedReason}
      >
        {/* Upload */}
        <label
          htmlFor="file-upload"
          className="btn-icon h-9 w-9 cursor-pointer rounded-full"
          style={{
            opacity: inputDisabled ? 0.5 : 1,
            pointerEvents: inputDisabled ? "none" : "auto",
          }}
          title={isUploadingFile ? "Enviando arquivo…" : "Anexar arquivo (máx. 20 MB)"}
          aria-label={isUploadingFile ? "Enviando arquivo" : "Anexar arquivo"}
        >
          <Upload size={18} />
        </label>

        <input
          id="file-upload"
          type="file"
          className="hidden"
          onChange={onUpload}
          disabled={inputDisabled}
        />

        {isUploadingFile && <span className="sr-only" role="status">Enviando arquivo…</span>}

        {canSendLocation && (
          <button
            type="button"
            onClick={onLocationSend}
            className="btn-icon h-9 w-9 rounded-full"
            disabled={inputDisabled}
            style={{ opacity: inputDisabled ? 0.5 : 1 }}
            title="Enviar localização do estabelecimento"
            aria-label="Enviar localização do estabelecimento"
          >
            <MapPin size={18} />
          </button>
        )}

        {/* Emoji button */}
        <button
          ref={emojiButtonRef}
          type="button"
          onClick={() => setShowEmojiPicker((v) => !v)}
          className="btn-icon h-9 w-9 rounded-full"
          disabled={inputDisabled}
          style={{ opacity: inputDisabled ? 0.5 : 1 }}
        >
          <Smile size={18} />
        </button>

        {/* Input */}
        {isRecording ? (
          <div className="flex flex-1 items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
            <span className="status-dot status-dot-danger animate-pulse" />
            <span className="text-slate-600">Gravando</span>
            <span className="ml-auto text-xs font-mono text-slate-500">
              {recordingTime}
            </span>
          </div>
        ) : (
          <textarea
            ref={textareaRef}
            value={text}
            rows={1}
            placeholder="Digite uma mensagem..."
            onChange={(e) => {
              setText(e.target.value);
              autoResize();
            }}
            disabled={inputDisabled}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !isMobileViewport) {
                e.preventDefault();

                if (!inputDisabled && text.trim()) {
                  onSubmit();
                  setText("");
                  setTimeout(autoResize, 0);
                }
              }
            }}
            className={`form-textarea flex-1 resize-none overflow-y-auto ${
              inputDisabled ? "bg-slate-100" : ""
            }`}
          />
        )}

        {isRecording ? (
          <>
            <button
              type="button"
              onClick={() => stopRecording(false)}
              disabled={isBlocked}
              className="btn-icon-danger rounded-full"
              style={{ opacity: isBlocked ? 0.5 : 1 }}
            >
              <Trash2 size={18} />
            </button>
            <button
              type="button"
              onClick={() => stopRecording(true)}
              disabled={isBlocked}
              className="btn-icon-success rounded-full"
              style={{ opacity: isBlocked ? 0.5 : 1 }}
            >
              <SendHorizontal size={18} />
            </button>
          </>
        ) : hasText ? (
          <button
            type="submit"
            disabled={isBlocked}
            className="btn-icon-success rounded-full"
            style={{ opacity: isBlocked ? 0.5 : 1 }}
          >
            <SendHorizontal size={18} />
          </button>
        ) : (
          <button
            type="button"
            onClick={startRecording}
            disabled={isBlocked}
            className="btn-icon rounded-full"
            style={{ opacity: isBlocked ? 0.5 : 1 }}
          >
            <Mic size={18} />
          </button>
        )}
      </form>

      {/* Emoji picker portal */}
      {showEmojiPicker &&
        createPortal(
          <div
            ref={pickerRef}
            className="fixed z-[9999]"
            style={{
              top: pickerPosition.top,
              left: pickerPosition.left,
            }}
          >
            <EmojiPicker
              onEmojiClick={handleEmojiClick}
              autoFocusSearch={false}
            />
          </div>,
          document.body,
        )}
    </>
  );
}
